import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';
import '../style/Header.css';
import logo from '../icons/logo.svg';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { User } from './types';
import { useUser } from '../context/UserContext';

const Header = () => {
  const { user, updateUserLanguage } = useUser();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // States for menus
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [langMenuEl, setLangMenuEl] = useState<null | HTMLElement>(null);

  // Menu open state
  const isMenuOpen = Boolean(anchorEl);
  const isLangMenuOpen = Boolean(langMenuEl);

  // Handlers for the main menu
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handlers for the language menu
  const handleLangMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLangMenuEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangMenuEl(null);
  };

  const handleLanguageChange = async (lang: string) => {
    try {
      const url = "/api/user/lang";
      const response = await fetch(url, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ language: lang }),
      });
      const updatedUser = await response.json() as User;
      updateUserLanguage(updatedUser.language);
      
      i18n.changeLanguage(lang);
      handleLangMenuClose();

    } catch (err) {
      console.error("Error joining event:", err);
    }
  };

  const handleLogout = async () => {
    try {
      const response = await fetch('/logout', {
        credentials: 'include',
      });

      if (response.ok) {
        localStorage.removeItem('authToken');
        window.location.href = '/';
      } else {
        console.error('Logout failed:', response.statusText);
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const handleVolunteers = () => {
    navigate('/volunteers');
    handleMenuClose();
  };

  const handleOurStory = () => {
    navigate('/our-story');
    handleMenuClose();
  };

  const handleHomePage = () => {
    navigate('/');
    handleMenuClose();
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: 'white',
        zIndex: 1000,
        paddingTop: '10px !important',
      }}
    >
      <Toolbar
        sx={{
          justifyContent: 'space-between',
          display: 'flex',
          alignItems: 'center',
          height: '64px',
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Logo"
          className="header-logo"
          sx={{ height: '50px', verticalAlign: 'middle' }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Main Menu */}
          <IconButton
            edge="end"
            aria-label="menu"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenuOpen}
            sx={{ color: 'black', verticalAlign: 'middle' }}
          >
            <MenuIcon sx={{ width: 40, height: 40 }} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleHomePage}>{t('userHome.menuTitle')}</MenuItem>
            <MenuItem onClick={handleOurStory}>{t('ourStory.title')}</MenuItem>
            <MenuItem onClick={handleVolunteers}>{t('volunteers.title')}</MenuItem>
            <MenuItem onClick={handleLogout}>
              {t('hostageList.header.exit')}
            </MenuItem>
          </Menu>
          {/* Language Selector */}
          <IconButton
            edge="end"
            aria-label="language-selector"
            aria-haspopup="true"
            onClick={handleLangMenuOpen}
            sx={{ color: 'black', verticalAlign: 'middle', marginRight: 2 }}
          >
            <LanguageIcon sx={{ width: 30, height: 30 }} />
          </IconButton>
          <Menu
            anchorEl={langMenuEl}
            open={isLangMenuOpen}
            onClose={handleLangMenuClose}
          >
            <MenuItem onClick={() => handleLanguageChange('he')}>עברית</MenuItem>
            <MenuItem onClick={() => handleLanguageChange('en')}>English</MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

const ConditionalHeader = ({
  setHeaderVisible,
}: {
  setHeaderVisible: (visible: boolean) => void;
}) => {
  const location = useLocation();
  const routesWithoutHeader = ['/', '/about', '/login', '/confirm-email', '*'];

  useEffect(() => {
    setHeaderVisible(!routesWithoutHeader.includes(location.pathname));
  }, [location.pathname, setHeaderVisible]);

  return (
    <>
      {!routesWithoutHeader.includes(location.pathname) && <Header />}
    </>
  );
};

export default ConditionalHeader;
